import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { legalActions, opp, type Color, type GameSnapshot, type MoveReview } from '@ic/engine';
import { requestReview } from '../lib/review';
import { GameView, type Position } from '../components/GameView';
import { LeaveGuard } from '../components/LeaveGuard';
import { Loader } from '../components/Loader';
import { Icon } from '../components/Icons';
import { auth } from '../lib/auth';
import { useStore } from '../lib/store';
import { onMessage, send } from '../lib/socket';
import { tcLabel } from './Play';

export function OnlineGame() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [g, setG] = useState<GameSnapshot | null>(null);
  const [recvAt, setRecvAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [eventsKey, setEventsKey] = useState(0);
  const [err, setErr] = useState('');
  const [nukeSignal, setNukeSignal] = useState(0);
  const [nukeBy, setNukeBy] = useState<Color | null>(null);
  const [flip, setFlip] = useState<Color | null>(null);
  const [copied, setCopied] = useState(false);
  // Positions seen since joining, for move review (index = moves played).
  const positions = useRef<(Position | undefined)[]>([]);
  const [reviews, setReviews] = useState<Record<number, MoveReview>>({});
  const { user: account } = useStore(auth);

  useEffect(() => {
    const off = onMessage((m) => {
      if (m.t === 'game' && m.game.id === id) {
        const n = m.game.moves.length;
        positions.current.length = Math.min(positions.current.length, n + 1);
        positions.current[n] = { state: m.game.state, lastAction: m.game.lastAction };
        positions.current[0] = { state: m.game.initial, lastAction: null };
        const before = positions.current[n - 1];
        const act = m.game.lastAction;
        // Hidden targets (an opponent's mine) cannot be rated.
        if (n > 0 && before && act && !(act.t === 'ability' && act.kind === 'placeMine' && act.target == null)) {
          void requestReview(before.state, act).then((r) => { if (r) setReviews((x) => ({ ...x, [n]: r })); });
        }
        setG((prev) => {
          if (!prev || prev.moves.length !== m.game.moves.length) setEventsKey((k) => k + 1);
          return m.game;
        });
        setRecvAt(Date.now());
      }
      if (m.t === 'nukeStart' && m.gameId === id) { setNukeBy(m.by); setNukeSignal(Date.now()); }
      if (m.t === 'error') setErr(m.message);
      if (m.t === 'gameStart' && m.gameId !== id) nav(`/game/${m.gameId}`);
    });
    // Logged in: take the free seat with the account. Guests first get the choice to log in.
    const u = auth.get().user;
    send({ t: 'join', gameId: id, sit: !!u && !u.guest });
    return () => { off(); send({ t: 'leave', gameId: id }); };
  }, [id, nav]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);

  const legal = useMemo(() => (g && g.status === 'active' && g.you === g.state.turn ? legalActions(g.state) : []), [g]);

  if (!g) {
    return (
      <div className="page page-loading">
        {err ? <div className="notice error">{err}</div> : <Loader label="Connecting to the game…" />}
      </div>
    );
  }

  const you = g.you;
  const signedIn = !!account && !account.guest;
  const orientation = flip ?? you ?? 'w';
  const clock = (c: Color) => {
    if (g.tc.initial === 0) return null;
    const base = g.clocks[c];
    return g.clocks.running === c ? Math.max(0, base - (now - recvAt)) : base;
  };
  const link = `${location.origin}/game/${g.id}`;
  const result = g.state.result;
  const players = {
    w: { name: g.white?.name ?? 'Waiting…', rating: g.white?.rating, online: g.white?.online, clockMs: clock('w'), running: g.clocks.running === 'w' },
    b: { name: g.black?.name ?? 'Waiting…', rating: g.black?.rating, online: g.black?.online, clockMs: clock('b'), running: g.clocks.running === 'b' },
  };

  return (
    <div className="page game-page">
      <LeaveGuard active={!!you && g.status === 'active'} onLeave={() => send({ t: 'resign', gameId: g.id })} />
      <GameView
        title={you ? 'Online game' : 'Spectating'}
        subtitle={`${g.state.rs.name} · ${tcLabel(g.tc)} · ${g.rated ? 'rated' : 'casual'} · ${g.spectators} watching`}
        state={g.state}
        initial={g.initial}
        orientation={orientation}
        legal={legal}
        interactive={!!you && g.status === 'active'}
        onAction={(action) => send({ t: 'action', gameId: g.id, action })}
        lastAction={g.lastAction}
        events={g.events}
        eventsKey={eventsKey}
        moves={g.moves}
        positions={positions.current}
        reviews={reviews}
        admin={account?.admin && you && g.status === 'active' ? { color: you, remoteNuke: true, onAction: (action) => send({ t: 'admin', gameId: g.id, action }) } : null}
        nukeSignal={nukeSignal}
        result={result}
        celebrate={!!you && !!result?.winner && result.winner === you}
        viewer={you ?? null}
        players={players}
        onFlip={() => setFlip(opp(orientation))}
        chat={{ messages: g.chat, onSend: (text) => send({ t: 'chat', gameId: g.id, text }) }}
        notice={<>
          {g.status === 'waiting' && !you && !signedIn && (
            <div className="notice">
              <b>You are invited to play.</b>
              <span>Log in to play with your account and rating, or play as a guest.</span>
              <div className="row gap wrap">
                <Link className="btn primary sm" to={`/login?next=/game/${g.id}`}>Log in</Link>
                <Link className="btn secondary sm" to={`/login?mode=signup&next=/game/${g.id}`}>Sign up</Link>
                <button className="btn ghost sm" onClick={() => send({ t: 'join', gameId: g.id, sit: true })}>Play as guest</button>
              </div>
            </div>
          )}
          {g.status === 'waiting' && you && (
            <div className="notice">
              <Loader size="sm" label="Waiting for an opponent" />
              Share this link:
              <div className="row gap"><input readOnly value={link} onFocus={(e) => e.target.select()} />
                <button className="btn secondary sm" onClick={() => { navigator.clipboard.writeText(link); setCopied(true); }}>{copied ? 'Copied' : 'Copy'}</button></div>
            </div>
          )}
          {g.drawOffer && you && g.drawOffer !== you && g.status === 'active' && (
            <div className="notice">Your opponent offers a draw.
              <button className="btn primary sm" onClick={() => send({ t: 'draw', gameId: g.id, op: 'accept' })}>Accept</button>
              <button className="btn ghost sm" onClick={() => send({ t: 'draw', gameId: g.id, op: 'decline' })}>Decline</button>
            </div>
          )}
          {g.takebackRequest && you && g.takebackRequest !== you && g.status === 'active' && (
            <div className="notice">Your opponent asks for a takeback.
              <button className="btn primary sm" onClick={() => send({ t: 'takeback', gameId: g.id, op: 'accept' })}>Accept</button>
              <button className="btn ghost sm" onClick={() => send({ t: 'takeback', gameId: g.id, op: 'decline' })}>Decline</button>
            </div>
          )}
          {err && <div className="error small">{err}</div>}
        </>}
        controls={you && g.status === 'active' ? <>
          {g.state.rs.rules.takebacks && <button className="btn secondary" disabled={!!g.takebackRequest || !g.moves.length} onClick={() => send({ t: 'takeback', gameId: g.id, op: 'request' })}><Icon name="undo" size={16} /> Takeback</button>}
          <button className="btn secondary" disabled={g.drawOffer === you} onClick={() => send({ t: 'draw', gameId: g.id, op: 'offer' })}>{g.drawOffer === you ? 'Draw offered' : 'Offer draw'}</button>
          <button className="btn danger" onClick={() => { if (confirm('Resign this game?')) send({ t: 'resign', gameId: g.id }); }}><Icon name="flag" size={16} /> Resign</button>
        </> : <button className="btn secondary" onClick={() => nav('/play/setup')}>New game</button>}
        endActions={<button className="btn primary" onClick={() => nav('/play/setup')}>New game</button>}
      />
    </div>
  );
}
